import React, { useEffect, useRef, useState } from 'react';
import { Typography, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PROJECT_PRODUCT, PROJECT_TYPE_ICONS } from '@src/constants/project';
import { useDispatch } from 'react-redux';
import { countTextLength } from '@src/services/tts';
import { normalizeNote, getProjectTypeKind } from '@src/services/project';
import actions from '@src/redux/actions';
import { updateProject, patchProjectConfig } from '@src/apis/project';
import {
  CheckOutlined,
  ClearOutlined,
  EditOutlined,
} from '@mui/icons-material';
import { calculateElapsedDateTime } from '@src/utils/date';
import Tooltip from '@src/components/Tooltip';
import SrtIcon from '@src/assets/images/srt-icon.svg';
import PresentationProjectIcon from '@src/assets/icons/project-presentation.svg';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { MAX_LENGTH_TITLE_PROJECT_EDIT } from '@src/constants/request';
import { StyledIconButton, StyledTitleProject } from './index.style';

const TOOLTIP_POPPER_PROPS = {
  popperOptions: {
    modifiers: [{ name: 'offset', options: { offset: [0, -6] } }],
  },
};

const TitleProject = ({
  title,
  updatedAt,
  blocks,
  id,
  refetchProjects,
  product,
  source,
  newUiProjectsEnabled,
  autoSaveEnabled = false,
  isExtracting,
  bulkDeleteProjectsEnabled = false,
}) => {
  const { t } = useTranslation();

  const ref = useRef(null);
  const dispatch = useDispatch();
  const [isHover, setIsHover] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [newTitle, setNewTitle] = useState(title);

  const handleOnMouseEnter = () => setIsHover(true);

  const handleOnMouseLeave = () => setIsHover(false);

  const handleClickTitle = (e) => e.stopPropagation();

  const handleChangeTitle = (e) => {
    e.stopPropagation();
    if (e.target.value.length > MAX_LENGTH_TITLE_PROJECT_EDIT) {
      dispatch(
        actions.noti.push({
          severity: 'warning',
          message: 'updateTitleMaxLength',
          value: MAX_LENGTH_TITLE_PROJECT_EDIT,
        }),
      );
      return;
    }
    setNewTitle(e.target.value);
  };

  const handleClickEdit = (e) => {
    e.stopPropagation();
    setEdit(true);
  };

  const handleCloseEdit = (e) => {
    if (e) e.stopPropagation();
    setEdit(false);
    setNewTitle(title);
  };

  const updateRequestTitle = async (e) => {
    try {
      e.stopPropagation();
      if (newTitle === title || newTitle.length <= 0) {
        setEdit(false);
        setNewTitle(title);
        return;
      }
      if (newTitle.length > MAX_LENGTH_TITLE_PROJECT_EDIT) {
        setEdit(false);
        setNewTitle(title);
        dispatch(
          actions.noti.push({
            severity: 'warning',
            message: 'updateTitleMaxLength',
            value: MAX_LENGTH_TITLE_PROJECT_EDIT,
          }),
        );
        return;
      }
      setIsLoading(true);

      let res;
      if (autoSaveEnabled) {
        // Match the in-project title save: a granular UPDATE_CONFIG (PATCH
        // /projects/:id) instead of a full project save with the whole blocks
        // payload.
        res = await patchProjectConfig(id, { title: newTitle });
      } else {
        const dubbingPayload = blocks.map((paragraph) => ({
          text: paragraph.text || '',
          title: paragraph.title,
          speed: paragraph.speed,
          elements: paragraph.elements,
          id: paragraph.id,
          startTime: paragraph.startTime,
          endTime: paragraph.endTime,
          speakerId: paragraph.speakerId,
        }));

        const ttsPayload = blocks.map((paragraph) => ({
          text: paragraph.text || '',
          title: paragraph.title,
          characters: countTextLength(paragraph.text || ''),
          speed: paragraph.speed,
          elements: paragraph.elements,
          currentNote: normalizeNote(paragraph.currentNote) || undefined,
          id: paragraph.id,
          ...(paragraph.voiceCode && { voiceCode: paragraph.voiceCode }),
          ...(paragraph.breakTime && { breakTime: paragraph.breakTime }),
          isSample: paragraph.isSample || false,
          ...(paragraph?.emphasisIntensity !== undefined
            ? { emphasisIntensity: paragraph?.emphasisIntensity }
            : {}),
        }));

        res = await updateProject(id, {
          title: newTitle,
          blocks:
            product === PROJECT_PRODUCT.DUBBING ? dubbingPayload : ttsPayload,
        });
      }
      if (res.status) {
        dispatch(
          actions.noti.push({
            severity: 'success',
            message: 'updateTitleProjectSuccess',
          }),
        );
        setNewTitle(newTitle);
        setIsLoading(false);
        setEdit(false);
        refetchProjects();
      }
    } catch (error) {
      setEdit(false);
      setIsLoading(false);
      setNewTitle(title);
      dispatch(
        actions.noti.push({
          severity: 'error',
          message:
            error?.response?.data?.error_message || 'updateTitleProjectError',
        }),
      );
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) handleCloseEdit();
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [title]);

  const typeIcon = PROJECT_TYPE_ICONS[getProjectTypeKind({ product, source })];

  const getIconProject = () => {
    if (newUiProjectsEnabled) {
      return <img src={typeIcon.icon} alt={t(typeIcon.label)} />;
    }

    if (product === PROJECT_PRODUCT.PRESENTATION) {
      return (
        <img src={PresentationProjectIcon} alt="presentation-project-icon" />
      );
    }

    return product === PROJECT_PRODUCT.DUBBING ? (
      <img src={SrtIcon} style={{ width: '32px' }} alt="srt-icon" />
    ) : (
      <LibraryBooksIcon />
    );
  };

  return (
    <StyledTitleProject
      newUiProjectsEnabled={newUiProjectsEnabled}
      bulkDeleteProjectsEnabled={bulkDeleteProjectsEnabled}
    >
      <div className="item">
        <Tooltip
          arrow
          title={t(typeIcon.label)}
          placement="top"
          PopperProps={TOOLTIP_POPPER_PROPS}
        >
          <div className="item-title-icon">{getIconProject()}</div>
        </Tooltip>
        <div
          ref={ref}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          className="item-title-wrapper"
        >
          {edit ? (
            <input
              className="edit-title"
              value={newTitle}
              maxLength={MAX_LENGTH_TITLE_PROJECT_EDIT + 1}
              onChange={handleChangeTitle}
              onClick={handleClickTitle}
            />
          ) : (
            <>
              <div className="display-title">
                <div className="title">
                  <div className="item-title">
                    <div className="text">{newTitle}</div>
                    {isHover && !isExtracting && (
                      <IconButton
                        className="edit-button"
                        onClick={handleClickEdit}
                        aria-label="delete"
                        type="button"
                        size="small"
                      >
                        <EditOutlined sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </div>
                  <Typography className="item-created-at">
                    {calculateElapsedDateTime(updatedAt, t)}
                  </Typography>
                </div>
              </div>
            </>
          )}

          {edit && (
            <div className="action">
              <StyledIconButton
                onClick={updateRequestTitle}
                disabled={isLoading}
                aria-label="agree"
                color="success"
                height="30px"
                width="30px"
              >
                <CheckOutlined />
              </StyledIconButton>
              <StyledIconButton
                onClick={handleCloseEdit}
                disabled={isLoading}
                aria-label="cancel"
                color="error"
                height="30px"
                width="30px"
              >
                <ClearOutlined />
              </StyledIconButton>
            </div>
          )}
        </div>
      </div>
    </StyledTitleProject>
  );
};

export default TitleProject;
